import { Box, Typography, Card, CardContent, Chip, Button, CircularProgress, Alert } from '@mui/material';
import { effectiveSeasonYear, gradeLabel } from '../../utils/footballHistory';
import { formatPositions } from '../../utils/footballPositions';
import { formatHeight } from '../../utils/heightUtils';
import { CURATED_TEST_TYPE_KEYS } from '../../utils/curatedTestTypes';
import { unitLabel } from '../../utils/testUnits';

// Phase C.8 — ATHLETE CARD FOUNDATION.
//
// Pure presentation: props in, JSX out. No Supabase calls, no
// authorization decisions, no react-router-dom import (kept directly
// unit-testable with RTL, same discipline as TeamTestingStation.js), no
// knowledge of WHO is viewing it — the guardian and coach pages each
// compose their own canonical data (getAthlete/getRosteredAthlete,
// listAthleteTeamMemberships, listTestingSessions, fetchTestTypes,
// derivePersonalBests, the C.8 photo service) and hand this component
// the SAME shape either way. One athlete truth, no GuardianAthleteCard/
// CoachAthleteCard split.
//
// Deliberately NOT rendered here, per this phase's explicit scope:
// Fast/Power/Agility/Nerd Rating, percentiles, Data Web/radar. The
// layout below (photo+identity row, then a physical row, then a PB
// row) is a plain vertical stack of independent sections specifically
// so a future performance-visualization section can be appended without
// restructuring anything already here — no empty placeholder panel is
// reserved for it now.
//
// membership is the ATHLETE's current/selected team_athletes-shaped row
// (already resolved by the caller — resolveCurrentMembership() for the
// guardian's multi-membership case, or simply the one row the coach
// page already has) — never fetched or re-derived here. null means "no
// active membership," rendered honestly, never fabricated.
export default function AthleteCard({
  athlete,
  membership,
  personalBests,
  photoUrl,
  photoLoading,
  canUpload,
  uploading,
  uploadError,
  onUploadClick,
  // Phase C.8 browser-acceptance follow-up — Remove Photo. Guardian-
  // only, same as canUpload/onUploadClick; canRemove is a SEPARATE flag
  // (never assumed equal to canUpload) so a future context could offer
  // one action without the other. The button itself only ever renders
  // when there's actually an active photo to remove (canRemove alone
  // is not enough) — there is never anything to remove from an
  // initials placeholder.
  canRemove,
  removing,
  onRemoveClick,
}) {
  const seasonYear = membership ? effectiveSeasonYear(membership) : null;
  const grade = gradeLabel(membership?.season_grade);
  const jersey = membership?.jersey_number === null || membership?.jersey_number === undefined
    ? null
    : `#${membership.jersey_number}`;
  const positions = membership ? formatPositions(membership) : '—';

  // Career-wide PBs (derivePersonalBests() already scans an athlete's
  // ENTIRE history) headline exactly the curated four tests
  // (utils/curatedTestTypes.js) — a deliberately stable, curated
  // presentation set, independent of any Program's testing
  // configuration (056) or which Program most recently tested this
  // athlete. Additional test_types — the three dormant canonical globals
  // (Fly 10, Vertical Jump, 5-10-5 Shuttle) AND any Program custom test
  // recorded through Team Testing — remain fully present in
  // derivePersonalBests()'s own output (it's already generic over any
  // test key, 056); they're simply not headlined on the compact Card.
  const pbByKey = new Map((personalBests || []).map(pb => [pb.testType.key, pb]));
  const headlinePbs = CURATED_TEST_TYPE_KEYS.map(key => pbByKey.get(key)).filter(Boolean);
  // A test with zero attempts yet still gets its own labeled "No PB yet"
  // tile below, so the four-tile layout never silently reflows to fewer
  // tiles just because an athlete hasn't tested for one of them.
  const missingKeys = CURATED_TEST_TYPE_KEYS.filter(key => !pbByKey.has(key));

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.85)', border: '1px solid #1e2a3a', overflow: 'visible' }}>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2.5, alignItems: { xs: 'center', sm: 'flex-start' } }}>
          <PhotoBox
            photoUrl={photoUrl}
            photoLoading={photoLoading}
            canUpload={canUpload}
            canRemove={canRemove}
            uploading={uploading}
            removing={removing}
            onUploadClick={onUploadClick}
            onRemoveClick={onRemoveClick}
            initials={`${athlete?.first_name?.[0] || ''}${athlete?.last_name?.[0] || ''}`}
          />

          <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              {athlete?.first_name} {athlete?.last_name}
            </Typography>

            {membership ? (
              <>
                <Typography sx={{ color: '#9eb8d0', fontWeight: 700, mt: 0.5 }}>
                  {membership.team?.team_name || 'Team'}{seasonYear ? ` · ${seasonYear}` : ''}
                </Typography>
                <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem', mt: 0.25 }}>
                  {grade || 'Grade —'} · {jersey || '#—'} · {positions}
                </Typography>
                <Chip
                  size="small"
                  label={membership.status === 'active' ? 'Active' : 'Inactive'}
                  sx={{
                    mt: 1,
                    bgcolor: membership.status === 'active' ? '#0a2a14' : '#131318',
                    color: membership.status === 'active' ? '#2ea84b' : '#6a8aa8',
                    fontWeight: 700,
                  }}
                />
              </>
            ) : (
              <Typography sx={{ color: '#6a8aa8', mt: 0.5 }}>Not currently rostered on a team.</Typography>
            )}
          </Box>
        </Box>

        {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}

        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700, mb: 1 }}>
            CURRENT PHYSICAL
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Stat label="Current Height" value={athlete?.height_in ? formatHeight(athlete.height_in) : '—'} />
            <Stat label="Current Weight" value={athlete?.weight_lb ? `${athlete.weight_lb} lb` : '—'} />
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700, mb: 1 }}>
            CAREER PERSONAL BESTS
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {headlinePbs.map(pb => (
              <Stat
                key={pb.testType.key}
                label={pb.testType.display_name}
                value={`${pb.value} ${unitLabel(pb.testType.unit)}`}
              />
            ))}
            {missingKeys.map(key => (
              <Stat key={key} label={key} value="No PB yet" muted />
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, muted }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 800, fontSize: muted ? '0.9rem' : '1.15rem', color: muted ? '#6a8aa8' : '#fff' }}>
        {value}
      </Typography>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem' }}>{label}</Typography>
    </Box>
  );
}

function PhotoBox({
  photoUrl, photoLoading, canUpload, canRemove, uploading, removing,
  onUploadClick, onRemoveClick, initials,
}) {
  const busy = uploading || removing;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      <Box
        sx={{
          width: 108, height: 108, borderRadius: 2, overflow: 'hidden',
          border: '1px solid #1e2a3a', backgroundColor: '#0a1622',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {photoLoading ? (
          <CircularProgress size={22} sx={{ color: '#2ea84b' }} />
        ) : photoUrl ? (
          <Box component="img" src={photoUrl} alt="Athlete Card" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Typography sx={{ color: '#3a5470', fontWeight: 900, fontSize: '1.8rem' }}>{initials || '—'}</Typography>
        )}
      </Box>
      {canUpload && (
        <Button
          size="small" onClick={onUploadClick} disabled={busy}
          aria-label={photoUrl ? 'Change Photo' : 'Add Season Photo'}
          sx={{ textTransform: 'none', color: '#6a8aa8', fontSize: '0.75rem', minWidth: 0, p: 0 }}
        >
          {uploading ? <CircularProgress size={14} /> : photoUrl ? 'Change Photo' : 'Add Season Photo'}
        </Button>
      )}
      {/* Only ever offered when there's an active photo to remove —
          never for an initials placeholder, regardless of canRemove. */}
      {canRemove && photoUrl && (
        <Button
          size="small" onClick={onRemoveClick} disabled={busy}
          aria-label="Remove Photo"
          sx={{ textTransform: 'none', color: '#f44336', fontSize: '0.75rem', minWidth: 0, p: 0 }}
        >
          {removing ? <CircularProgress size={14} /> : 'Remove Photo'}
        </Button>
      )}
    </Box>
  );
}
